import {
  ChangeEvent,
  HTMLAttributes,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  Ref,
  RefAttributes,
  type JSX,
  useCallback,
  useRef,
} from "react";

import MuiAutocomplete, {
  AutocompleteCloseReason,
  AutocompleteOwnerState,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  AutocompleteValue,
} from "@mui/material/Autocomplete";
import { Theme } from "@mui/material/styles";
import { AutocompleteGetTagProps } from "@mui/material/useAutocomplete";
import { FieldValues, useController } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../common/helpers/validators";
import { useFormInputRefs } from "../../../common/hooks/useFormInputRefs";
import { DashboardCheckbox } from "../DashboardCheckbox/DashboardCheckbox";
import { DashboardIcon } from "../Icon";
import { DashboardTextField } from "../TextField/DashboardTextField";

import { AutocompletePaperProvider } from "./AutocompletePaperContext";
import { DefaultAutocompletePaper } from "./DefaultAutocompletePaper";
import { AutocompleteProps, AutocompleteOption, FormAutocompleteProps, BaseAutocompleteProps } from "./types";
import { useTagRendering } from "./useTagRendering";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useCurrentInputVibe } from "@/common/hooks/useCurrentInputVibe";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useValidators } from "@/common/hooks/useValidators";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { TruncatedTextWithTooltip } from "@/components/TruncatedTextWithTooltip/TruncatedTextWithTooltip";
import { ChevronDown, Cross as Close } from "@/icons/outlined";

function AutocompleteInner<
  Value,
  T extends FieldValues,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  { control, rules, name, ...rest }: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <FormAutocomplete<T, Value, Multiple, DisableClearable, FreeSolo>
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
    />
  ) : (
    <BaseAutocomplete<Value, Multiple, DisableClearable, FreeSolo> {...rest} name={name} ref={ref} />
  );
}

AutocompleteInner.displayName = "Autocomplete";

export const DashboardAutocomplete = forwardRef(AutocompleteInner) as <
  Value = string | AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  T extends FieldValues = FieldValues,
>(
  props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function FormAutocompleteInner<
  Value = string | AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  T extends FieldValues = FieldValues,
>(
  {
    control,
    defaultValue,
    name,
    onChange,
    rules,
    noAsterisk,
    ...rest
  }: FormAutocompleteProps<T, Value, Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);
  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as never,
    rules: validators,
  });

  const handleChange = useCallback(
    (value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | undefined) => {
      if (typeof value === "string") {
        return;
      }
      onChange?.(value);
      field.onChange(value);
      return value;
    },
    [field, onChange]
  );

  return (
    <BaseAutocomplete
      {...rest}
      inputRef={field.ref}
      onChange={handleChange}
      value={field.value as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>}
      ref={ref}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

FormAutocompleteInner.displayName = "FormAutocomplete";

const FormAutocomplete = forwardRef(FormAutocompleteInner) as unknown as <
  T extends FieldValues,
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: FormAutocompleteProps<T, Value, Multiple, DisableClearable, FreeSolo> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const isOptionEqualToValue = (option: string | AutocompleteOption, value: string | AutocompleteOption) =>
  typeof option === "string" || typeof value === "string" ? option === value : option.value === value?.value;

function BaseAutocompleteInner<
  Value = string | AutocompleteOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    vibe,
    value,
    disableAutocomplete,
    error,
    errorMessage,
    onChange,
    label,
    multiple,
    inputRef: externalInputRef,
    placeholder,
    "data-test": dataTest,
    autoFocus,
    hideDropdownIcon,
    hideClearIcon,
    checkbox = false,
    limitTags = 3,
    endAdornment,
    popupIcon,
    inputValue,
    name,
    disableCloseOnSelect = true,
    open,
    onOpen,
    onClose,
    PaperComponent,
    freeSolo,
    renderTags: externalRenderTags,
    onFocus,
    chipProps,
    sx,
    ...rest
  }: BaseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLInputElement>
) {
  // If no PaperComponent is provided, we only call the onChange callback when the user clicks the apply button
  const usingSubmitPattern = !PaperComponent && Boolean(multiple);

  const currentInputVibe = useCurrentInputVibe(vibe);
  const { t } = useTranslate();

  const [internalIsOpen, setInternalIsOpen] = useState(Boolean(open));

  // In a multiselect Autocomplete, want to persist the search value even after selection
  // Autocomplete component doesn't support this, so we need to manage it ourselves
  const [internalInputValue, setInternalInputValue] = useState(inputValue || "");

  // We maintain the values internally, and propagate them on apply
  const [syncedInternalValue, setSyncedInternalValue] = useState(value);
  const [internalValue, setInternalValue] = useState(value);

  // Ref to the actual input element for blurring
  const internalInputRef = useRef<HTMLInputElement>(null);

  // Combine both external and internal input refs
  const refCallBack = useFormInputRefs({ inputRefs: [internalInputRef, externalInputRef] });

  const hideTags = internalIsOpen && usingSubmitPattern && multiple;

  const { renderTags: internalRenderTags } = useTagRendering({
    tags: multiple ? (internalValue as (string | AutocompleteOption<string>)[]) || [] : [],
    hideTags,
    ignoreInputWidth: usingSubmitPattern && multiple,
    chipProps,
  });

  useEffect(() => {
    if (Boolean(open) !== internalIsOpen) {
      setInternalIsOpen(Boolean(open));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Sync when value changes externally
    setSyncedInternalValue(value);
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    setInternalInputValue(inputValue || "");
  }, [inputValue]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (multiple && event?.target) {
        setInternalInputValue((event.target as HTMLInputElement).value);
      }
    },
    [multiple]
  );

  const handleOnOpen = useCallback(() => {
    // We should only handle the open state internally if open is undefined
    if (open === undefined) {
      setInternalIsOpen(true);
    }
    onOpen?.();
  }, [onOpen, open]);

  const closeAutocomplete = useCallback(() => {
    if (multiple && !freeSolo) {
      setInternalInputValue("");
    }
    // We should only handle the open state internally if open is undefined
    if (open === undefined) {
      setInternalIsOpen(false);
    }
    onClose?.();
    setTimeout(() => {
      // Blur the actual input element
      internalInputRef.current?.blur();
    }, 0);
  }, [freeSolo, multiple, onClose, open]);

  const handleOnClose = useCallback(
    (_event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
      if (usingSubmitPattern && reason !== "escape" && internalValue !== syncedInternalValue) {
        onChange?.(internalValue);
        setSyncedInternalValue(internalValue);
      } else {
        setInternalValue(syncedInternalValue);
      }
      closeAutocomplete();
    },
    [usingSubmitPattern, internalValue, syncedInternalValue, onChange, closeAutocomplete]
  );

  const handleOnApply = useCallback(() => {
    onChange?.(internalValue);
    setSyncedInternalValue(internalValue);
    closeAutocomplete();
  }, [closeAutocomplete, internalValue, onChange]);

  const handleOnClear = useCallback(() => {
    const newValue = multiple ? ([] as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>) : undefined;
    onChange?.(newValue);
    setSyncedInternalValue(newValue);
    setInternalValue(newValue);
    closeAutocomplete();
  }, [closeAutocomplete, multiple, onChange]);

  const handleOnRemoveTag = useCallback(
    (optionToRemove: string | AutocompleteOption) => {
      if (!multiple || !Array.isArray(internalValue)) {
        return;
      }

      const newValue = (internalValue as (string | AutocompleteOption)[]).filter((item) => {
        if (typeof item === "string" && typeof optionToRemove === "string") {
          return item !== optionToRemove;
        }
        if (typeof item === "object" && typeof optionToRemove === "object") {
          return item.value !== optionToRemove.value;
        }
        return true; // Keep items that don't match the type being removed
      });

      setInternalValue(newValue as AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>);
    },
    [multiple, internalValue]
  );

  const renderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement>,
      option: string | AutocompleteOption,
      { selected }: { selected: boolean }
    ) => {
      const label = typeof option === "string" ? option : option.label;
      const key = typeof option === "string" ? option : (option.value as string);
      return (
        <li {...props} key={key}>
          {checkbox && (
            <DashboardCheckbox
              checked={selected}
              sx={(theme) => ({
                marginRight: theme.spacing(1),
              })}
              data-test="base-autocomplete-option-checkbox"
            />
          )}
          <TruncatedTextWithTooltip
            tooltipProps={{
              title: label,
              placement: "right",
              "data-test": "base-autocomplete-option-tooltip",
            }}
            data-test="base-autocomplete-option-label"
          >
            {label}
          </TruncatedTextWithTooltip>
        </li>
      );
    },
    [checkbox]
  );

  const textFieldSlotProps = useMemo(
    () =>
      hideTags
        ? {
            input: {
              sx: (theme: Theme) => ({ marginLeft: 0, paddingLeft: theme.spacing(1) }),
            },
          }
        : undefined,
    [hideTags]
  );

  const renderInput = useCallback(
    ({
      InputProps: { endAdornment: inputEndAdornment, ref, startAdornment },
      ...rest
    }: AutocompleteRenderInputParams) => (
      <DashboardTextField
        {...rest}
        {...(multiple ? { onChange: handleInputChange } : undefined)}
        vibe={currentInputVibe}
        sx={(theme: Theme) => ({
          "& .MuiInputBase-root": {
            overflow: "hidden",
            display: "flex",
            flexWrap: "nowrap",
            ...(hideTags ? { paddingLeft: 0 } : {}),
          },
          "& .MuiInputBase-input": {
            flexShrink: 0,
            width: 10,
            ...(hideTags ? { marginLeft: 0, paddingLeft: theme.spacing(1) } : {}),
          },
        })}
        {...(textFieldSlotProps ? { slotProps: textFieldSlotProps } : {})}
        data-test={dataTest}
        disableAutocomplete={disableAutocomplete}
        endAdornment={
          <>
            {inputEndAdornment}
            {endAdornment}
          </>
        }
        error={error}
        errorMessage={errorMessage}
        label={label}
        ref={ref}
        startAdornment={startAdornment}
        inputRef={refCallBack}
        placeholder={placeholder}
        value={
          multiple
            ? internalInputValue
            : typeof value === "string"
              ? value
              : (value as AutocompleteOption<string>)?.value
        }
        autoFocus={autoFocus}
        name={name}
      />
    ),
    [
      multiple,
      handleInputChange,
      currentInputVibe,
      textFieldSlotProps,
      dataTest,
      disableAutocomplete,
      endAdornment,
      error,
      errorMessage,
      label,
      refCallBack,
      placeholder,
      internalInputValue,
      value,
      autoFocus,
      name,
      hideTags,
    ]
  );

  return (
    <AutocompletePaperProvider
      multiple={multiple || false}
      internalValue={internalValue}
      syncedInternalValue={syncedInternalValue}
      internalIsOpen={internalIsOpen}
      usingSubmitPattern={usingSubmitPattern}
      externalRenderTags={
        externalRenderTags as (
          value: (string | AutocompleteOption<unknown>)[],
          getTagProps: AutocompleteGetTagProps
        ) => React.ReactNode
      }
      onApply={handleOnApply}
      onClear={handleOnClear}
      onRemoveTag={handleOnRemoveTag}
      chipProps={chipProps}
    >
      <MuiAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
        noOptionsText={t("common", "noOptions")}
        {...rest}
        clearIcon={
          hideClearIcon ? null : (
            <DashboardIcon
              size="small"
              sx={[
                ...(currentInputVibe === "form"
                  ? [
                      (theme: Theme) => ({
                        fill: theme.palette.text.form.quiet,
                        color: theme.palette.text.form.quiet,
                      }),
                    ]
                  : []),
              ]}
              onClick={(e) => {
                e.stopPropagation();
                handleOnClear();
              }}
            >
              <Close />
            </DashboardIcon>
          )
        }
        popupIcon={
          hideDropdownIcon ? null : (
            <DashboardIcon
              size="small"
              sx={[
                ...(currentInputVibe === "form"
                  ? [
                      (theme: Theme) => ({
                        fill: theme.palette.text.form.quiet,
                        color: theme.palette.text.form.quiet,
                      }),
                    ]
                  : []),
              ]}
            >
              {popupIcon ?? <ChevronDown />}
            </DashboardIcon>
          )
        }
        renderInput={renderInput}
        ref={ref}
        multiple={multiple}
        renderOption={
          (rest.renderOption || renderOption) as (
            props: React.HTMLAttributes<HTMLLIElement>,
            option: Value,
            state: AutocompleteRenderOptionState,
            ownerState: AutocompleteOwnerState<Value, Multiple, DisableClearable, FreeSolo>
          ) => React.ReactNode
        }
        renderTags={
          externalRenderTags ||
          (internalRenderTags as (value: Value[], getTagProps: AutocompleteGetTagProps) => React.ReactNode)
        }
        isOptionEqualToValue={
          rest.isOptionEqualToValue || (isOptionEqualToValue as (option: Value, value: Value) => boolean)
        }
        inputValue={multiple ? internalInputValue : undefined}
        disableCloseOnSelect={disableCloseOnSelect}
        open={internalIsOpen}
        onOpen={handleOnOpen}
        onClose={handleOnClose}
        onFocus={(event) => {
          handleOnOpen();
          onFocus?.(event);
        }}
        PaperComponent={PaperComponent || (usingSubmitPattern ? DefaultAutocompletePaper : undefined)}
        onChange={(_evt, newValue) => {
          if (!usingSubmitPattern) {
            onChange?.(newValue);
          } else {
            setInternalValue(newValue);
          }
        }}
        value={internalValue}
        limitTags={limitTags}
        freeSolo={freeSolo}
        data-test="base-autocomplete"
        sx={mergeSx(
          (theme: Theme) => ({
            "& .MuiAutocomplete-endAdornment": {
              position: "static",
              top: "unset",
              right: "unset",
              bottom: "unset",
              left: "unset",
              display: "flex",
              padding: 0,
              transform: "none",
            },
            "& .MuiAutocomplete-clearIndicator": {
              borderRadius: theme.radius(0.75),
              margin: 0,
              transition: "all 200ms ease-in",
              opacity: 1,
              transform: "scale(1)",
              animation: "clearIndicatorAppear 200ms ease-in",
              "@keyframes clearIndicatorAppear": {
                from: {
                  opacity: 0,
                  transform: "scale(0) rotate(-90deg)",
                },
                to: {
                  opacity: 1,
                  transform: "scale(1) rotate(0deg)",
                },
              },
            },
            "& .MuiAutocomplete-popupIndicator": {
              borderRadius: theme.radius(0.75),
              padding: theme.spacing(0.5),
              flexShrink: 0,
              ...(popupIcon ? { transform: "none" } : {}),
            },
            "& .MuiAutocomplete-option": {
              ...theme.typography.body2,
              color: theme.palette.text.neutral.moderate,
              fill: theme.palette.text.neutral.moderate,
              padding: theme.spacing(1, 2),
              overflow: "hidden",
              "&[aria-selected='true']": {
                ...theme.typography.subtitle2,
                background: theme.palette.primary.lightest,
              },
              "&.Mui-focused": {
                background: theme.palette.surface.neutral.supershy,
                backgroundColor: theme.palette.surface.neutral.supershy,
              },
              ...(currentInputVibe === "form"
                ? {
                    color: theme.palette.text.form.moderate,
                    fill: theme.palette.text.form.moderate,
                    "&.Mui-focused": {
                      background: theme.palette.surface.form.supershy,
                      backgroundColor: theme.palette.surface.form.supershy,
                    },
                  }
                : {}),
            },
            "& .MuiAutocomplete-paper": {
              boxShadow: theme.boxShadow.base.mid,
            },
            "& .MuiAutocomplete-input": {
              flexShrink: 0,
              width: 10,
            },
            "& .MuiAutocomplete-inputRoot": {
              overflow: "hidden",
              display: "flex",
              flexWrap: "nowrap",
            },
            "& .MuiAutocomplete-listbox": {
              maxHeight: 200,
            },
          }),
          sx
        )}
      />
    </AutocompletePaperProvider>
  );
}
BaseAutocompleteInner.displayName = "BaseAutocomplete";

const BaseAutocomplete = forwardRef(BaseAutocompleteInner) as unknown as <
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>(
  props: BaseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> & RefAttributes<HTMLInputElement>
) => JSX.Element;
